import { apiSlice } from 'src/features/api/api-slice';
import { RTKQueryTag } from 'src/common/constants';
import { ApiResponse } from 'src/common/interfaces';
import { CategorizationRule } from 'src/common/interfaces/categorization-dashboard';

import {
  GetActivityRequest,
  GetActivityResponse,
  GetContextResponse,
  GetRulesRequest,
  GetRulesResponse,
  GetRuleTitlesResponse,
} from './categorization.types';

export const categorizationDashboardApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCategories: builder.query<string[], void>({
      query: () => ({
        url: 'admin/categorization-dashboard/categories',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<string[]>) => response.data || [],
      providesTags: [RTKQueryTag.CategorizationCategories],
    }),

    getSources: builder.query<Record<string, string[]>, void>({
      query: () => ({
        url: 'admin/categorization-dashboard/sources',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<Record<string, string[]>>) => response.data || {},
      providesTags: [RTKQueryTag.CategorizationSources],
    }),

    getContexts: builder.query<GetContextResponse[], void>({
      query: () => ({
        url: 'admin/categorization-dashboard/contexts',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<GetContextResponse[]>) => response.data || [],
      providesTags: [RTKQueryTag.CategorizationContexts],
    }),

    getRules: builder.query<GetRulesResponse, GetRulesRequest>({
      query: ({ term, categories, types, rooms, skip, limit }) => {
        const params = new URLSearchParams();

        if (term) {
          params.append('term', term);
        }

        if (skip) {
          params.append('skip', skip.toString());
        }

        if (limit) {
          params.append('limit', limit.toString());
        }

        if (categories) {
          for (const category of categories) {
            params.append('categories[]', category);
          }
        }

        if (types) {
          for (const type of types) {
            params.append('types[]', type);
          }
        }

        if (rooms) {
          for (const room of rooms) {
            params.append('rooms[]', room);
          }
        }

        return {
          url: 'admin/categorization-dashboard/rules',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: ApiResponse<GetRulesResponse>) => response.data || { rules: [], count: 0 },
      providesTags: [RTKQueryTag.CategorizationRules],
    }),

    getRule: builder.query<ApiResponse<CategorizationRule> | undefined, string>({
      query: (id: string) => ({
        url: `admin/categorization-dashboard/rules/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: RTKQueryTag.CategorizationRules, id }],
    }),

    saveRule: builder.mutation<CategorizationRule, CategorizationRule>({
      query: (payload: CategorizationRule) => ({
        url: `admin/categorization-dashboard/rules${payload._id ? `/${payload._id}` : ''}`,
        method: payload._id ? 'PUT' : 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error) =>
        !error && result
          ? [
              RTKQueryTag.CategorizationRules,
              { type: RTKQueryTag.CategorizationRules, id: result._id },
              RTKQueryTag.CategorizationRuleTitles,
              RTKQueryTag.RoomBlueprintRules,
              { type: RTKQueryTag.RoomBlueprintRules, ruleId: result._id },
              RTKQueryTag.CategorizationCategories,
            ]
          : [],
    }),

    deleteRule: builder.mutation({
      query: (id: string) => ({
        url: `admin/categorization-dashboard/rules/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, id) =>
        error
          ? []
          : [
              RTKQueryTag.CategorizationRules,
              { type: RTKQueryTag.CategorizationRules, id },
              RTKQueryTag.CategorizationRuleTitles,
              RTKQueryTag.RoomBlueprintRules,
              { type: RTKQueryTag.RoomBlueprintRules, ruleId: id },
              RTKQueryTag.CategorizationCategories,
            ],
    }),

    getRuleTitles: builder.query<GetRuleTitlesResponse, string | undefined>({
      query: (id?: string) => ({
        url: 'admin/categorization-dashboard/titles',
        method: 'GET',
        params: id ? { excludeId: id } : undefined,
      }),
      transformResponse: (response: ApiResponse<GetRuleTitlesResponse>) => response.data || { rules: [] },
      providesTags: [RTKQueryTag.CategorizationRuleTitles],
    }),

    getActivity: builder.query<GetActivityResponse[], GetActivityRequest>({
      query: (params) => ({
        url: 'admin/categorization-dashboard/activities',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<GetActivityResponse[]>) => response.data || [],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetSourcesQuery,
  useGetContextsQuery,
  useGetRulesQuery,
  useGetRuleQuery,
  useSaveRuleMutation,
  useDeleteRuleMutation,
  useGetRuleTitlesQuery,
  useLazyGetActivityQuery,
} = categorizationDashboardApiSlice;
